import { useState } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FieldSet,
  Heading,
  Icon,
  Label,
  LabeledControl,
  Legend,
  Modal,
  ModalTrigger,
  Paragraph,
  Radio,
  TextButton,
} from '@zillow/constellation-10';
import { IconArrowRightCircleFilled, IconHeartOutline } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import emailUtils from '../../../utils/emailUtils';

import { getListingRoute, getListingSourcesRoute } from '../../../constants/urls';

import { useAuth } from '../../AuthProvider';

const CHOICES = {
  INCORRECT: {
    id: 'INCORRECT',
    text: 'My listing has incorrect or outdated information.',
  },
  SOURCE: {
    id: 'SOURCE',
    text: 'I want to change my listing source.',
  },
  SUPPORT: {
    id: 'SUPPORT',
    text: 'Neither of these. I need to contact support.',
  },
};

const ListingIssuesModalTrigger = ({ showIcon = true }: { showIcon?: boolean }) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [choiceId, setChoiceId] = useState<string | null>(null);
  const [showFixIncorrectInformation, setShowFixIncorrectInformation] = useState(false);

  const heading = showFixIncorrectInformation ? 'Fix incorrect or outdated information' : 'Listing issues';
  const email = emailUtils.getHelpEmail(user?.isMultifamilyCustomer, user?.isSingleFamilyEnterpriseCustomer);
  const hasManualDupe = listingDetails?.topDupesDetails && listingDetails?.topDupesDetails?.hasManualDupe;

  const handleSubmit = () => {
    if (choiceId === CHOICES.INCORRECT.id) {
      setShowFixIncorrectInformation(true);
    } else if (choiceId === CHOICES.SOURCE.id) {
      router.push(getListingSourcesRoute({ listingAlias }));
    } else {
      const email = emailUtils.getHelpEmail(user?.isMultifamilyCustomer, user?.isSingleFamilyEnterpriseCustomer);
      window.location.href = `mailto:${email}`;
    }
  };

  const renderNextButton = () => {
    return (
      <Button emphasis="filled" disabled={choiceId === null} onClick={handleSubmit}>
        Next
      </Button>
    );
  };

  const handleGoToManualListing = () => {
    if (listingDetails?.topDupesDetails?.topDupeManualListingAlias) {
      router.push(getListingRoute({ listingAlias: listingDetails.topDupesDetails.topDupeManualListingAlias }));
    }
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${email}`;
  };

  let title = 'Automatically create a new version in Zillow Rental Manager';
  let text = `We’ll make an editable copy of the listing. You can change it in Zillow Rental Manager,
        then publish the new listing.`;
  let cta = 'Fix listing';
  let ctaClick = null;

  if (hasManualDupe) {
    title = 'Update your listing in Zillow Rental Manager';
    text = 'Go to the Zillow Rental Manager version of the listing, update fields and then publish the listing.';
    cta = 'Update your listing';
    ctaClick = handleGoToManualListing;
  }

  const renderCta = () => <TextButton onClick={() => ctaClick?.()}>{cta}</TextButton>;

  return (
    <Modal
      header={<Heading level={2}>{heading}</Heading>}
      body={
        <>
          {showFixIncorrectInformation ? (
            <>
              <Paragraph css={{ textStyle: 'heading-sm' }}>{title}</Paragraph>
              <Paragraph css={{ marginBlock: '400' }}>{text}</Paragraph>
              <Modal.Close>
                {!hasManualDupe ? (
                  <ModalTrigger multiModalTriggerId="TransferFeedListingModal">{renderCta()}</ModalTrigger>
                ) : (
                  renderCta()
                )}
              </Modal.Close>
              <Divider css={{ backgroundColor: 'border.accent.yellow.hero', width: '142px', marginBlock: '400' }} />
              <Paragraph css={{ textStyle: 'heading-sm' }}>Or you can contact support</Paragraph>
              <Paragraph css={{ marginBlock: '400' }}>Get help with questions about your listing.</Paragraph>
              <Modal.Close>
                <TextButton onClick={handleEmailClick}>Email Zillow support</TextButton>
              </Modal.Close>
            </>
          ) : (
            <>
              <Icon
                size="xl"
                css={{ color: 'icon.accent.blue.hero', marginBottom: '400' }}
                render={<IconHeartOutline />}
              />
              <Box css={{ paddingBottom: '400' }}>
                <FieldSet legend={<Legend>How can we help you?</Legend>}>
                  {(Object.keys(CHOICES) as Array<keyof typeof CHOICES>).map((choiceKey) => {
                    const choice = CHOICES[choiceKey];
                    return (
                      <LabeledControl
                        key={choice.id}
                        controlId={`LabeledControl-choice-${choice.id}`}
                        label={<Label>{choice.text}</Label>}
                        control={
                          <Radio
                            name={`LabeledControl-choice-${choice.id}`}
                            checked={choice.id === choiceId}
                            onChange={() => setChoiceId(choice.id)}
                          />
                        }
                      />
                    );
                  })}
                </FieldSet>
              </Box>
            </>
          )}
        </>
      }
      footer={
        !showFixIncorrectInformation ? (
          <ButtonGroup aria-label="Listing issues actions">
            <Modal.Close>
              <TextButton>Cancel</TextButton>
            </Modal.Close>
            {choiceId === CHOICES.INCORRECT.id ? renderNextButton() : <Modal.Close>{renderNextButton()}</Modal.Close>}
          </ButtonGroup>
        ) : undefined
      }
      size={{ base: 'fullScreen', md: 'md' }}
      trigger={
        <TextButton
          css={{ marginBottom: showIcon ? '200' : '600' }}
          {...(showIcon
            ? { icon: <Icon size="md" render={<IconArrowRightCircleFilled />} />, iconPosition: 'end' }
            : {})}
        >
          Listing issues?
        </TextButton>
      }
    />
  );
};

export { ListingIssuesModalTrigger };
export default ListingIssuesModalTrigger;
